import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux";
import apiClient from "../../api/client";
import { ChatSession } from "../../types/chat.types";

interface AdminChatSession extends ChatSession {
  user_email?: string;
}

export const AdminChatsPage: React.FC = () => {
  const [sessions, setSessions] = useState<AdminChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterLLM, setFilterLLM] = useState<string>("all");
  const [filterUser, setFilterUser] = useState<string>("");

  useEffect(() => {
    fetchAdminSessions();
  }, [filterLLM, filterUser]);

  const fetchAdminSessions = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filterLLM !== "all") params.llm_provider = filterLLM;
      if (filterUser) params.user_id = filterUser;

      const response = await apiClient.get<{ sessions: AdminChatSession[]; total: number }>(
        "/admin/sessions",
        { params }
      );
      setSessions(response.data.sessions);
    } catch (error) {
      console.error("Failed to fetch admin sessions:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">
            💬 Chat Management
          </h1>
          <p className="text-slate-400">
            View and manage all user chat sessions
          </p>
        </div>

        {/* Filters */}
        <div className="bg-slate-800 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 gap-4">
            {/* LLM Filter */}
            <div>
              <label className="block text-slate-300 text-sm mb-2">
                Filter by LLM Provider
              </label>
              <select
                value={filterLLM}
                onChange={(e) => setFilterLLM(e.target.value)}
                className="w-full px-4 py-2 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All LLMs</option>
                <option value="grok">Grok</option>
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="google">Google</option>
              </select>
            </div>

            {/* User Filter */}
            <div>
              <label className="block text-slate-300 text-sm mb-2">
                Filter by User ID
              </label>
              <input
                type="text"
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                placeholder="Enter user ID..."
                className="w-full px-4 py-2 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Sessions Table */}
        <div className="bg-slate-800 rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400">
              <div className="animate-spin text-4xl mb-4">⏳</div>
              <p>Loading sessions...</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <p className="text-4xl mb-4">📭</p>
              <p>No sessions found</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">
                    Session
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">
                    LLM
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">
                    Messages
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">
                    Last Activity
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {sessions.map((session) => (
                  <tr key={session.id} className="hover:bg-slate-700/50">
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">
                        {session.title || "Untitled"}
                      </div>
                      <div className="text-xs text-slate-400">
                        {session.id.slice(0, 8)}...
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-300">
                        {session.user_email || session.user_id.slice(0, 8)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs px-2 py-1 bg-blue-900/50 text-blue-300 rounded w-fit">
                          {session.llm_provider || "N/A"}
                        </span>
                        {session.llm_model && (
                          <span className="text-xs text-slate-400">
                            {session.llm_model}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-300">
                      {session.message_count}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-sm">
                      {new Date(session.last_message_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-4 gap-4">
          <div className="bg-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Total Sessions</p>
            <p className="text-2xl font-bold text-white mt-1">
              {sessions.length}
            </p>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Grok</p>
            <p className="text-2xl font-bold text-white mt-1">
              {sessions.filter((s) => s.llm_provider === "grok").length}
            </p>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">OpenAI</p>
            <p className="text-2xl font-bold text-white mt-1">
              {sessions.filter((s) => s.llm_provider === "openai").length}
            </p>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Other</p>
            <p className="text-2xl font-bold text-white mt-1">
              {
                sessions.filter(
                  (s) =>
                    s.llm_provider !== "grok" && s.llm_provider !== "openai"
                ).length
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
